
// Static import
declare global {
  interface Window {
    IntersectionObserverEntry: any
  }
  interface HTMLDocument {
    closest?: (selector: string) => null
  }
}

/* eslint-disable complexity */
function shouldLoadPolyfills(): boolean {
  if (typeof Object.assign !== "function") { return true }
  if (typeof Object.create !== "function") { return true }
  if (typeof Object.defineProperty !== "function") { return true }
  if (typeof Object.defineProperties !== "function") { return true }
  if (typeof Object.keys !== "function") { return true }

  if (typeof Object.entries !== "function") { return true }
  if (typeof Object.values !== "function") { return true }

  if (typeof Array.isArray !== "function") { return true }
  if (typeof Array.from !== "function") { return true }
  if (typeof Array.prototype.forEach !== "function") { return true }
  if (typeof Array.prototype.map !== "function") { return true }
  if (typeof Array.prototype.join !== "function") { return true }
  if (typeof Array.prototype.indexOf !== "function") { return true }
  if (typeof Array.prototype.slice !== "function") { return true }
  if (typeof Array.prototype.every !== "function") { return true }
  if (typeof Array.prototype.some !== "function") { return true }
  if (typeof Array.prototype.includes !== "function") { return true }
  if (typeof Array.prototype.find !== "function") { return true }
  if (typeof Array.prototype.reduce !== "function") { return true }
  if (typeof Array.prototype.filter !== "function") { return true }

  if (typeof String.prototype.trim !== "function") { return true }
  if (typeof String.prototype.includes !== "function") { return true }
  if (typeof String.prototype.replaceAll !== "function") { return true }

  if (typeof Number.isNaN !== "function") { return true }

  if (typeof Set !== "function") { return true }

  // Used by https://github.com/instantpage/instant.page
  // https://dom.spec.whatwg.org/#dom-element-closest
  if (typeof Element.prototype.closest !== "function") { return true }

  // https://babeljs.io/docs/en/babel-plugin-transform-es2015-for-of
  if (typeof Symbol !== "function") { return true }

  if (typeof Promise !== "function") { return true }

  if (typeof fetch !== "function") { return true }
  if (typeof AbortController !== "function") { return true }
  if (typeof URLSearchParams !== "function") { return true }

  // Class list support detection
  // Code from https://github.com/eligrey/classList.js
  if (
    !("classList" in document.createElement("_"))
    || document.createElementNS
    && !("classList" in document.createElementNS("http://www.w3.org/2000/svg", "g"))
  ) { return true }

  // Got error = need event polyfill
  // Code from https://github.com/lifaon74/events-polyfill
  try {
    new Event("event", { bubbles: true, cancelable: true })
  }
  catch (ex) { return true }

  return false
}
/* eslint-enable complexity */

function shouldLoadIntersectionObserverPolyfill(): boolean {
  // Conditional code from
  // https://github.com/w3c/IntersectionObserver
  return !("IntersectionObserver" in window &&
    "IntersectionObserverEntry" in window &&
    "intersectionRatio" in window.IntersectionObserverEntry.prototype)
}

const loadPolyfillsSometimes = (): Promise<any> => {
  return new Promise((resolve) => {
    if (shouldLoadPolyfills()) {
      import(
        "./optional_content"
      )
      .then(() => resolve())
    }
    else {
      resolve()
    }
  })
  .then(() => {
    if (shouldLoadIntersectionObserverPolyfill()) {
      import(
        // Since all mobile Safari needs this (even 12)
        // We include the polyfill file in main chunks (one of them)
        "intersection-observer"
      )
    }

    // Return nothing, that's fine
    return null
  })
  .then(() => {
    const HTMLDocumentPrototype = window.HTMLDocument.prototype
    // Polyfill for `HTMLDocument#closest`
    // Normally only `Element#closest` is needed
    // But the lib `instant.page` this project using
    // https://github.com/instantpage/instant.page/blob/v5.1.0/instantpage.js
    // sometimes encounter `event.target` as `HTMLDocument`
    // and raises `t.target.closest is not a function`
    // So this is just to suppress that error
    if (typeof HTMLDocumentPrototype.closest !== "function") {
      HTMLDocumentPrototype.closest = function closest(_selector) {
        return null
      }
    }

    // Return nothing, that's fine
    return null
  })
}

export {
  loadPolyfillsSometimes,
}
